import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Shield, 
  CheckCircle, 
  AlertCircle,
  Phone,
  Mail,
  Calendar,
  Scale,
  Eye,
  Keyboard,
  Volume2,
  Type,
  Contrast,
  FileText,
  ExternalLink
} from 'lucide-react';

export default function AccessibilityStatement() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';

  const currentDate = new Date().toLocaleDateString(isRTL ? 'he-IL' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const complianceFeatures = [
    {
      icon: Type,
      title: t('accessibilityStatement.features.fontSize.title', 'התאמת גודל גופן'),
      description: t('accessibilityStatement.features.fontSize.description', 'אפשרות להגדלת טקסט עד 200% ללא פגיעה בפונקציונליות'),
      compliant: true
    },
    {
      icon: Contrast,
      title: t('accessibilityStatement.features.contrast.title', 'ניגודיות צבעים'),
      description: t('accessibilityStatement.features.contrast.description', 'יחס ניגודיות מינימלי של 4.5:1 לטקסט רגיל ו-3:1 לטקסט גדול'),
      compliant: true
    },
    {
      icon: Keyboard,
      title: t('accessibilityStatement.features.keyboard.title', 'ניווט מקלדת'),
      description: t('accessibilityStatement.features.keyboard.description', 'כל הפונקציות זמינות באמצעות מקלדת בלבד'),
      compliant: true
    },
    {
      icon: Volume2,
      title: t('accessibilityStatement.features.screenReader.title', 'תמיכה בקוראי מסך'),
      description: t('accessibilityStatement.features.screenReader.description', 'תואם לקוראי מסך NVDA, JAWS ו-VoiceOver'),
      compliant: true
    },
    {
      icon: Eye,
      title: t('accessibilityStatement.features.images.title', 'תיאור תמונות'),
      description: t('accessibilityStatement.features.images.description', 'כל התמונות כוללות טקסט חלופי מתאים'),
      compliant: true
    },
    {
      icon: FileText,
      title: t('accessibilityStatement.features.structure.title', 'מבנה סמנטי'),
      description: t('accessibilityStatement.features.structure.description', 'שימוש נכון בתגי HTML וכותרות היררכיות'),
      compliant: true
    }
  ];

  const keyboardShortcuts = [
    { key: 'Alt + S', description: t('accessibilityStatement.shortcuts.skipToContent', 'דלג לתוכן הראשי') },
    { key: 'Tab', description: t('accessibilityStatement.shortcuts.navigate', 'נווט בין אלמנטים') },
    { key: 'Enter / Space', description: t('accessibilityStatement.shortcuts.activate', 'הפעל קישור או כפתור') },
    { key: 'Esc', description: t('accessibilityStatement.shortcuts.close', 'סגור תפריטים פתוחים') },
    { key: 'Shift + Tab', description: t('accessibilityStatement.shortcuts.navigateBack', 'נווט אחורה') }
  ];

  return (
    <div className={`min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className={`mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <ArrowLeft className={`h-4 w-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
            {t('accessibilityStatement.back', 'חזרה')}
          </Button>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                {t('accessibilityStatement.title', 'הצהרת נגישות')}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                {t('accessibilityStatement.subtitle', 'מחויבות לנגישות דיגיטלית לפי החוק הישראלי')}
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Calendar className="h-4 w-4" />
            {t('accessibilityStatement.lastUpdated', 'עדכון אחרון')}: {currentDate}
          </div>
        </div>

        {/* Legal Compliance */}
        <Card className="mb-8 border-green-200 dark:border-green-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
              <CheckCircle className="h-5 w-5" />
              {t('accessibilityStatement.compliance.title', 'תאימות חוקית')}
            </CardTitle>
            <CardDescription>
              {t('accessibilityStatement.compliance.description', 'האתר עומד בדרישות החוק הישראלי לנגישות')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg">
                <Scale className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-800 dark:text-green-300">
                    {t('accessibilityStatement.law.title', 'חוק שוויון זכויות לאנשים עם מוגבלות')}
                  </h3>
                  <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                    {t('accessibilityStatement.law.year', 'התשנ"ח-1998')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-800 dark:text-blue-300">
                    {t('accessibilityStatement.standard.title', 'התקן הישראלי ת"י 5568')}
                  </h3>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                    {t('accessibilityStatement.standard.description', 'מבוסס על WCAG 2.1 ברמה AA')}
                  </p>
                </div>
              </div>
            </div>
            
            {/* <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h4 className="font-semibold mb-2">
                {t('accessibilityStatement.penalties.title', 'עונשים על אי-עמידה בחוק')}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('accessibilityStatement.penalties.description', 'אי-עמידה בדרישות הנגישות עלולה להוביל לקנס של עד 50,000 ₪ ולתביעות אזרחיות.')}
              </p>
            </div> */}
          </CardContent>
        </Card>

        {/* Commitment Statement */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t('accessibilityStatement.commitment.title', 'מחויבותנו לנגישות')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-gray dark:prose-invert max-w-none">
              <p>
                {t('accessibilityStatement.commitment.text1', 'אנו ב-FINGENT מחויבים להפוך את האתר שלנו לנגיש לכל המשתמשים, כולל אנשים עם מוגבלויות. אנו פועלים על פי עקרונות הנגישות הדיגיטלית ומיישמים את הדרישות החוקיות הישראליות.')}
              </p>
              <p>
                {t('accessibilityStatement.commitment.text2', 'האתר פותח בהתאם לתקן הישראלי ת"י 5568 ולהנחיות WCAG 2.1 ברמה AA, המהווים את הסטנדרט הבינלאומי לנגישות אתרי אינטרנט.')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Accessibility Features */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t('accessibilityStatement.features.title', 'תכונות נגישות באתר')}</CardTitle>
            <CardDescription>
              {t('accessibilityStatement.features.description', 'האתר כולל תכונות נגישות מתקדמות המותאמות לצרכים שונים')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {complianceFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-3 p-4 rounded-lg border bg-card">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{feature.title}</h3>
                      {feature.compliant && (
                        <Badge variant="secondary" className="text-xs">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {t('accessibilityStatement.compliant', 'תואם')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Keyboard Navigation */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Keyboard className="h-5 w-5" />
              {t('accessibilityStatement.keyboard.title', 'ניווט מקלדת')}
            </CardTitle>
            <CardDescription>
              {t('accessibilityStatement.keyboard.description', 'כל הפונקציות באתר זמינות באמצעות מקלדת בלבד')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              {keyboardShortcuts.map((shortcut, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm font-mono">
                    {shortcut.key}
                  </kbd>
                  <span className="text-sm text-muted-foreground">{shortcut.description}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Testing and Validation */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t('accessibilityStatement.testing.title', 'בדיקות ואימות')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold">
                    {t('accessibilityStatement.testing.automated.title', 'בדיקות אוטומטיות')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibilityStatement.testing.automated.description', 'האתר נבדק באמצעות כלים אוטומטיים לזיהוי בעיות נגישות')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold">
                    {t('accessibilityStatement.testing.manual.title', 'בדיקות ידניות')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibilityStatement.testing.manual.description', 'בדיקות ידניות עם קוראי מסך ומקלדת בלבד')}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold">
                    {t('accessibilityStatement.testing.screenReader.title', 'בדיקות קורא מסך')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {t('accessibilityStatement.testing.screenReader.description', 'נבדק עם NVDA גרסה 2024.1 ו-JAWS 2024')}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Known Issues */}
        <Card className="mb-8 border-yellow-200 dark:border-yellow-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
              <AlertCircle className="h-5 w-5" />
              {t('accessibilityStatement.issues.title', 'בעיות ידועות ומגבלות')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t('accessibilityStatement.issues.description', 'אנו עובדים ללא הרף לשיפור נגישות האתר. הבעיות הבאות ידועות לנו ואנו פועלים לפתרונן:')}
              </p>
              
              <div className="bg-yellow-50 dark:bg-yellow-950/20 p-4 rounded-lg">
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600 dark:text-yellow-400">•</span>
                    {t('accessibilityStatement.issues.item1', 'חלק מהתמונות הדקורטיביות עדיין לא כוללות תיאור מלא')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-600 dark:text-yellow-400">•</span>
                    {t('accessibilityStatement.issues.item2', 'כמה טפסים מורכבים עדיין בתהליך שיפור לנגישות מלאה')}
                  </li>
                </ul>
              </div>
              
              <p className="text-sm text-muted-foreground">
                {t('accessibilityStatement.issues.timeline', 'אנו מתכננים לפתור בעיות אלו עד סוף הרבעון הנוכחי.')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t('accessibilityStatement.contact.title', 'יצירת קשר בנושא נגישות')}</CardTitle>
            <CardDescription>
              {t('accessibilityStatement.contact.description', 'אם נתקלת בבעיית נגישות או זקוק לסיוע, אנא פנה אלינו')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold">{t('accessibilityStatement.contact.email.title', 'דוא"ל')}</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('accessibilityStatement.contact.email.description', 'לפניות בנושא נגישות')}
                  </p>
                  <a 
                    href="mailto:accessibility@fingent.com" 
                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                  >
                    accessibility@fingent.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                <Phone className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold">{t('accessibilityStatement.contact.phone.title', 'טלפון')}</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('accessibilityStatement.contact.phone.description', 'ימים א\'-ה\', 09:00-17:00')}
                  </p>
                  <a 
                    href="tel:+972-3-1234567" 
                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                  >
                    03-1234567
                  </a>
                </div>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                <strong>{t('accessibilityStatement.contact.response.title', 'זמן תגובה:')}</strong>{' '}
                {t('accessibilityStatement.contact.response.time', 'אנו מתחייבים להשיב לפניות נגישות תוך 48 שעות בימי עבודה.')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Legal Information */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>{t('accessibilityStatement.legal.title', 'מידע משפטי')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-gray dark:prose-invert max-w-none text-sm">
              <h4>{t('accessibilityStatement.legal.lawTitle', 'חוק שוויון זכויות לאנשים עם מוגבלות, התשנ"ח-1998')}</h4>
              <p>
                {t('accessibilityStatement.legal.lawDescription', 'החוק קובע כי אסור לפגוע בכבודו או בחירותו של אדם עם מוגבלות, ויש לנקוט באמצעים סבירים כדי לאפשר לו להשתמש בשירות או להיכנס למקום הפתוח לציבור.')}
              </p>
              
              <h4>{t('accessibilityStatement.legal.standardTitle', 'התקן הישראלי ת"י 5568')}</h4>
              <p>
                {t('accessibilityStatement.legal.standardDescription', 'התקן מבוסס על הנחיות WCAG 2.1 ברמה AA ומתאים לתנאים הישראליים. התקן כולל דרישות לנגישות תכנים באינטרנט, כולל טקסט, תמונות, אודיו, וידאו וטפסים.')}
              </p>
              
              <h4>{t('accessibilityStatement.legal.enforcementTitle', 'אכיפה וסנקציות')}</h4>
              <p>
                {t('accessibilityStatement.legal.enforcementDescription', 'אי-עמידה בדרישות הנגישות מהווה עבירה אזרחית שעונשה קנס של עד 50,000 ₪. כמו כן, ניתן להגיש תביעה ייצוגית בנושא.')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* External Resources */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              {t('accessibilityStatement.resources.title', 'משאבים נוספים')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              <a
                href="https://www.gov.il/he/departments/guides/accessibility_statement"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="text-sm">{t('accessibilityStatement.resources.govGuide', 'מדריך הממשלה לנגישות')}</span>
              </a>
              
              <a
                href="https://www.w3.org/WAI/WCAG21/quickref/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 border rounded-lg hover:bg-muted transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="text-sm">{t('accessibilityStatement.resources.wcag', 'הנחיות WCAG 2.1')}</span>
              </a>
              
              <Button variant="outline" onClick={() => navigate('/contact')} className="justify-start">
                <Mail className="h-4 w-4 mr-2" />
                {t('accessibilityStatement.resources.contact', 'צור קשר')}
              </Button>
              
              <Button variant="outline" onClick={() => navigate('/accessibility')} className="justify-start">
                <Shield className="h-4 w-4 mr-2" />
                {t('accessibilityStatement.resources.settings', 'הגדרות נגישות')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 